import { useCallback, useEffect, useState } from 'react'
import { AlertTriangle, KeyRound, ShieldOff, Timer } from 'lucide-react'
import { explain } from '@/shared/api/problem'
import { ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { EmptyRow, ErpTable, Panel, SectionHeader, Td, Th } from '@/components/erp/primitives'
import { CollectedAt, Pill, label } from './components/primitives'
import { listRuns, type Run } from './api'

/**
 * The run monitor — the operator's window.
 *
 * ## The four SKIPPED statuses are ACTIONABLE STATES, not errors
 *
 * "No credential", "rate limited", "robots disallows" and "stalled" each have a thing
 * somebody does about them, and each is rendered with that thing next to it. A monitor that
 * painted them all red and called them failures would train operators to ignore the colour —
 * which is exactly what happens to a screen where everything is an error.
 */
const STATUS_GUIDANCE: Record<string, { icon: typeof KeyRound; hint: string; tone: string }> = {
  SKIPPED_NO_CREDENTIAL: {
    icon: KeyRound,
    hint: 'Add or activate a credential for this source’s integration.',
    tone: 'text-amber-600',
  },
  SKIPPED_RATE_LIMITED: {
    icon: Timer,
    hint: 'The provider asked us to wait. It will be retried with backoff — nothing to do.',
    tone: 'text-sky-600',
  },
  SKIPPED_POLICY: {
    icon: ShieldOff,
    hint: 'robots.txt disallows this fetch. Use a licensed feed or drop the source.',
    tone: 'text-slate-600',
  },
  STALLED: {
    icon: AlertTriangle,
    hint: 'The run started and never finished. Check the provider, then re-run.',
    tone: 'text-rose-600',
  },
  FAILED: {
    icon: AlertTriangle,
    hint: 'Three consecutive failures disable the plan and raise this.',
    tone: 'text-rose-600',
  },
}

export function RunsPage() {
  const [runs, setRuns] = useState<Run[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [statusCode, setStatusCode] = useState('')

  const load = useCallback(async () => {
    try {
      setError(null)
      setRuns(await listRuns({ statusCode: statusCode || undefined }))
    } catch (err) {
      setError(explain(err))
    }
  }, [statusCode])

  useEffect(() => { void load() }, [load])

  if (error && !runs) return <ErrorStrip message={error} />
  if (!runs) return <LoadingBlock label="Loading runs" />

  return (
    <div className="space-y-3">
      <SectionHeader
        title="Run monitor"
        description="Every collection run and why it ended the way it did. A skip is something
                     to fix, not an error to hide."
      />

      <Panel>
        <div className="flex items-end gap-2 p-2">
          <label className="flex flex-col gap-0.5">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
              Status
            </span>
            <select value={statusCode} onChange={(e) => setStatusCode(e.target.value)}
                    className="h-7 rounded border border-input bg-background px-2 text-[12px]">
              <option value="">All</option>
              {['COMPLETED', 'PARTIAL', 'FAILED', 'SKIPPED_NO_CREDENTIAL',
                'SKIPPED_RATE_LIMITED', 'SKIPPED_POLICY', 'STALLED', 'RUNNING', 'QUEUED']
                .map((s) => <option key={s} value={s}>{label(s)}</option>)}
            </select>
          </label>
        </div>
      </Panel>

      <Panel>
        <ErpTable head={<>
          <Th>Status</Th>
          <Th>Collector</Th>
          <Th className="axis-hide-sm">Trigger</Th>
          <Th className="text-right">Found</Th>
          <Th className="text-right">New</Th>
          <Th className="text-right axis-hide-sm">Duplicate</Th>
          <Th className="text-right axis-hide-sm">Rejected</Th>
          <Th className="axis-hide-sm">Robots</Th>
          <Th>Started</Th>
        </>}>
          {runs.length === 0 && (
            <EmptyRow colSpan={9} message="No runs yet." />
          )}
          {runs.map((run) => {
            const guidance = STATUS_GUIDANCE[run.statusCode]
            const Icon = guidance?.icon
            return (
              <tr key={run.id} className="hover:bg-muted/40">
                <Td>
                  <div className="flex items-center gap-1">
                    {Icon && <Icon className={`size-3.5 ${guidance.tone}`} />}
                    <Pill className="border-border">{label(run.statusCode)}</Pill>
                  </div>
                  {guidance && (
                    <span className="block text-[10px] text-muted-foreground">
                      {guidance.hint}
                    </span>
                  )}
                  {run.errorDetail && !guidance && (
                    <span className="block text-[10px] text-muted-foreground">
                      {run.errorDetail}
                    </span>
                  )}
                </Td>
                <Td><span className="font-mono text-[11px]">{run.collectorCode}</span></Td>
                <Td className="axis-hide-sm">
                  <span className="text-[11px]">{label(run.triggerCode)}</span>
                </Td>
                <Td className="text-right tabular-nums">{run.itemsFound}</Td>
                <Td className="text-right tabular-nums">{run.itemsNew}</Td>
                <Td className="text-right tabular-nums axis-hide-sm">{run.itemsDuplicate}</Td>
                <Td className="text-right tabular-nums axis-hide-sm">{run.itemsRejected}</Td>
                <Td className="axis-hide-sm">
                  <span className="text-[11px] text-muted-foreground">
                    {run.robotsDecision ? label(run.robotsDecision) : '—'}
                  </span>
                </Td>
                {/* started_at, not queued_at — the stall detector keys on it too. */}
                <Td><CollectedAt at={run.startedAt} /></Td>
              </tr>
            )
          })}
        </ErpTable>
      </Panel>
    </div>
  )
}
